import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '@/lib/api';
import ArenaxLayout from './ArenaxLayout';
import ArenaxLiveNow from './ArenaxLiveNow';
import ArenaxCityPulseTicker from './ArenaxCityPulseTicker';
import ArenaxGamesNearYou from './ArenaxGamesNearYou';
import ArenaxPerfectForYou from './ArenaxPerfectForYou';
import ArenaxTonightInCity from './ArenaxTonightInCity';
import {
  Sparkles, MapPin, ArrowRight, Search, Zap, Trophy, Users, Calendar,
  Brain, Globe, Star, ChevronRight, Activity, Crosshair, Radio,
} from 'lucide-react';

const SPORT_ICONS = {
  Football: '⚽', Cricket: '🏏', Badminton: '🏸', Tennis: '🎾', Basketball: '🏀',
  Hockey: '🏑', Volleyball: '🏐', Pickleball: '🏓', 'Table Tennis': '🏓',
  Squash: '🎾', Athletics: '🏃', Other: '🏟️',
};

export default function ArenaxLanding() {
  const [data, setData] = useState({ stats: { venues: 0, bookings: 0, players: 0, sports_supported: 12 }, supported_sports: [], featured_venues: [] });
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/arenax/landing').then(r => setData(r.data)).catch(() => {});
  }, []);

  return (
    <ArenaxLayout>
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="arenax-hero">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-1/4 w-[480px] h-[480px] rounded-full blur-[120px] opacity-30" style={{ background: 'radial-gradient(circle, #06b6d4, transparent 70%)' }} />
          <div className="absolute top-40 right-1/4 w-[400px] h-[400px] rounded-full blur-[120px] opacity-25" style={{ background: 'radial-gradient(circle, #f59e0b, transparent 70%)' }} />
          <div className="absolute inset-0 opacity-[0.04]" style={{
            backgroundImage: 'linear-gradient(rgba(6,182,212,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(6,182,212,0.5) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-24">
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6 border border-cyan-500/30"
              style={{ background: 'linear-gradient(90deg, rgba(6,182,212,0.12), rgba(245,158,11,0.08))' }} data-testid="arenax-hero-badge">
              <Sparkles className="h-3.5 w-3.5 text-cyan-300" />
              <span className="text-[11px] tracking-[0.25em] font-bold text-cyan-200">AI-NATIVE PAY & PLAY ECOSYSTEM</span>
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.05] tracking-tight mb-6" data-testid="arenax-hero-headline">
              <span className="text-slate-100">Step on the field.</span><br />
              <span className="arenax-hero-gradient-text">
                ArenaX runs the rest.
              </span>
            </h1>

            <p className="text-lg sm:text-xl text-slate-400 max-w-2xl leading-relaxed mb-9" data-testid="arenax-hero-subtitle">
              Discover venues. Lock slots. Build squads. Find your game — powered by India's
              smartest sports coordination layer, built on top of <span className="text-cyan-300">Out-Play.in</span>.
            </p>

            <div className="flex flex-wrap gap-3 mb-12">
              <Link to="/arenax/venues" data-testid="arenax-hero-cta-discover">
                <button className="px-7 py-3.5 rounded-xl font-bold text-slate-950 text-sm flex items-center gap-2 shadow-2xl transition-all hover:-translate-y-1"
                  style={{ background: 'var(--ax-primary-gradient)', boxShadow: '0 16px 48px -12px rgba(6,182,212,0.6)' }}>
                  <Search className="h-4 w-4" />
                  Discover Venues
                  <ArrowRight className="h-4 w-4" />
                </button>
              </Link>
              <Link to="/arenax/auth/register" data-testid="arenax-hero-cta-join">
                <button className="px-7 py-3.5 rounded-xl font-bold text-cyan-300 text-sm border border-cyan-500/40 hover:bg-cyan-500/10 transition-all">
                  Join as Player
                </button>
              </Link>
            </div>

            {/* LIVE STATS BAND */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-3xl">
              <StatBlock value={data.stats.venues} label="Venues live" icon={MapPin} color="#06b6d4" />
              <StatBlock value={data.stats.players} label="Players" icon={Users} color="#f59e0b" />
              <StatBlock value={data.stats.bookings} label="Bookings" icon={Calendar} color="#ec4899" />
              <StatBlock value={data.stats.sports_supported} label="Sports" icon={Trophy} color="#10b981" />
            </div>
          </motion.div>

          {/* HERO STAGE MOCKUP */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.3 }}
            className="mt-16 relative" data-testid="arenax-hero-mockup">
            <div
              className="relative rounded-3xl border overflow-hidden p-6 sm:p-8 backdrop-blur-sm"
              style={{
                background: 'var(--ax-card-gradient)',
                borderColor: 'var(--ax-border-bold)',
              }}
            >
              <div className="absolute top-3 right-3 flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-rose-500" />
                <span className="h-2 w-2 rounded-full bg-amber-400" />
                <span className="h-2 w-2 rounded-full bg-emerald-400" />
              </div>
              <HeroMiniLive />
            </div>
          </motion.div>
        </div>
      </section>

      {/* PHASE 128 — Live in the City ticker — supply-side civic motion */}
      <section className="relative pt-6 px-5 sm:px-8" data-testid="arenax-city-pulse-section">
        <div className="max-w-7xl mx-auto">
          <ArenaxCityPulseTicker />
        </div>
      </section>

      {/* LIVE NOW ENGINE — real data, refreshes every 30 s */}
      <ArenaxLiveNow />

      {/* PERFECT FOR YOU — Phase 2C AI matchmaking (auth-aware: shows CTA for guests) */}
      <ArenaxPerfectForYou />

      {/* GAMES STARTING NEAR YOU — Phase 2A activation engine */}
      <ArenaxGamesNearYou />

      {/* TONIGHT IN THE CITY — Phase 2L PR 1 narrative ticker */}
      <section className="relative pt-6 pb-12 px-5 sm:px-8" data-testid="arenax-tonight-section">
        <div className="max-w-7xl mx-auto">
          <ArenaxTonightInCity variant="strip" />
        </div>
      </section>

      {/* FEATURE PILLARS */}
      <section className="relative py-20 px-5 sm:px-8" data-testid="arenax-features">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="text-[11px] tracking-[0.4em] text-amber-300/90 font-bold mb-3">THE ARENAX EDGE</div>
            <h2 className="text-3xl sm:text-5xl font-black text-slate-100 mb-3">
              Not a booking panel.
              <span className="block" style={{ background: 'linear-gradient(120deg, #22d3ee, #f59e0b)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                A sports operating system.
              </span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <FeatureCard icon={Crosshair} title="Smart Slot Engine" desc="Drag-drop schedules, recurring rules, dynamic pricing, holiday windows — configured in seconds." color="#06b6d4" />
            <FeatureCard icon={Activity} title="Real-time Booking" desc="Atomic slot locking with 10-min checkout windows. No double bookings, no race conditions." color="#f59e0b" />
            <FeatureCard icon={Brain} title="AI Matchmaking" desc="Find compatible players by sport, skill, location, and chemistry — coming in MVP-2." color="#ec4899" />
            <FeatureCard icon={Users} title="Squads & Communities" desc="Build crews. Run weekly games. Join leagues. Share reels — the sports network India deserves." color="#a855f7" />
            <FeatureCard icon={Trophy} title="Tournaments" desc="Bracket builders, live scoring, prize pools — from college fest to corporate league." color="#10b981" />
            <FeatureCard icon={Globe} title="Plugged into Out-Play" desc="Existing academies onboard in one click. RBAC, payments, webhooks, identity — all reused." color="#06b6d4" />
          </div>
        </div>
      </section>

      {/* FEATURED VENUES */}
      <section className="relative py-20 px-5 sm:px-8 border-t border-cyan-500/10" data-testid="arenax-venues-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-9">
            <div>
              <div className="text-[11px] tracking-[0.4em] text-cyan-300/90 font-bold mb-2">SCOUTED FOR YOU</div>
              <h2 className="text-3xl sm:text-4xl font-black text-slate-100">Featured Venues</h2>
            </div>
            <Link to="/arenax/venues" className="text-xs text-cyan-300 hover:text-cyan-200 flex items-center gap-1" data-testid="arenax-see-all-venues">
              See all <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          {data.featured_venues.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {data.featured_venues.map(v => <VenueCard key={v.id} venue={v} />)}
            </div>
          ) : (
            <div className="rounded-3xl border border-dashed border-cyan-500/20 p-12 text-center">
              <Sparkles className="h-10 w-10 text-cyan-400/50 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-slate-200 mb-2">Building your local arena</h3>
              <p className="text-sm text-slate-500 max-w-md mx-auto mb-6">
                Venues are coming online weekly. Academies plug into ArenaX from their Out-Play dashboard — one click and they're live.
              </p>
              <Link to="/login" data-testid="arenax-cta-academy-onboard">
                <button className="px-5 py-2.5 rounded-lg text-xs font-bold text-slate-950" style={{ background: 'var(--ax-primary-gradient)' }}>
                  Academy owner? Enable ArenaX →
                </button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* SPORTS GRID */}
      <section className="py-20 px-5 sm:px-8 border-t border-cyan-500/10" data-testid="arenax-sports">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <div className="text-[11px] tracking-[0.4em] text-amber-300/90 font-bold mb-2">EVERY SPORT, ONE TAP AWAY</div>
            <h2 className="text-3xl sm:text-4xl font-black text-slate-100">Sports you can play tonight</h2>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
            {(data.supported_sports || []).map(s => (
              <Link key={s} to={`/arenax/venues?sport=${encodeURIComponent(s)}`} className="aspect-square rounded-2xl border border-cyan-500/15 hover:border-cyan-500/40 flex flex-col items-center justify-center gap-1.5 transition-all hover:-translate-y-1 group"
                style={{ background: 'linear-gradient(135deg, rgba(6,182,212,0.06), var(--ax-card-bg-soft))' }}
                data-testid={`arenax-sport-${s.toLowerCase().replace(/ /g, '-')}`}>
                <span className="text-3xl">{SPORT_ICONS[s] || '🏟️'}</span>
                <span className="text-[11px] font-bold text-slate-300 group-hover:text-cyan-300">{s}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="py-20 px-5 sm:px-8" data-testid="arenax-cta-section">
        <div className="max-w-5xl mx-auto rounded-3xl overflow-hidden relative" style={{
          background: 'linear-gradient(135deg, rgba(6,182,212,0.15) 0%, rgba(245,158,11,0.12) 100%)',
          border: '1px solid rgba(6,182,212,0.3)',
        }}>
          <div className="absolute inset-0 opacity-30" style={{
            backgroundImage: 'radial-gradient(circle at 80% 50%, rgba(245,158,11,0.4) 0%, transparent 60%)',
          }} />
          <div className="relative p-10 sm:p-14 text-center">
            <h2 className="text-3xl sm:text-5xl font-black text-slate-100 mb-4 leading-tight">
              Your next game is one tap away.
            </h2>
            <p className="text-base text-slate-300 max-w-2xl mx-auto mb-8">
              Join thousands of players already on ArenaX. Free to sign up. Pay only when you play.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link to="/arenax/auth/register" data-testid="arenax-bottom-cta-register">
                <button className="px-8 py-4 rounded-xl font-bold text-slate-950 text-sm shadow-2xl"
                  style={{ background: 'var(--ax-primary-gradient)', boxShadow: '0 20px 48px -12px rgba(6,182,212,0.6)' }}>
                  Create Player Account
                </button>
              </Link>
              <Link to="/arenax/venues" data-testid="arenax-bottom-cta-browse">
                <button className="px-8 py-4 rounded-xl font-bold text-cyan-300 text-sm border border-cyan-500/40 hover:bg-cyan-500/10">
                  Browse Venues
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </ArenaxLayout>
  );
}

function StatBlock({ value, label, icon: Icon, color }) {
  return (
    <div
      className="rounded-xl border px-4 py-3 backdrop-blur-sm"
      style={{
        background: 'var(--ax-card-bg-soft)',
        borderColor: 'var(--ax-border-quiet)',
      }}
    >
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-3.5 w-3.5" style={{ color }} />
        <div
          className="text-[10px] tracking-[0.2em] font-semibold uppercase"
          style={{ color: `hsl(var(--ax-text-muted))` }}
        >
          {label}
        </div>
      </div>
      <div className="text-2xl font-black" style={{ color }}>{value || 0}</div>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, desc, color }) {
  return (
    <div className="rounded-2xl p-6 border border-slate-800 hover:border-cyan-500/40 transition-all group hover:-translate-y-1"
      style={{ background: 'linear-gradient(160deg, rgba(8,145,178,0.10), var(--ax-card-bg-soft))' }}>
      <div className="h-11 w-11 rounded-xl flex items-center justify-center mb-4" style={{ background: `${color}22`, border: `1px solid ${color}40` }}>
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
      <h3 className="text-base font-bold text-slate-100 mb-1.5">{title}</h3>
      <p className="text-sm text-slate-400 leading-relaxed">{desc}</p>
    </div>
  );
}

function VenueCard({ venue }) {
  return (
    <Link to={`/arenax/venues/${venue.slug || venue.id}`} className="block rounded-2xl border border-slate-800 hover:border-cyan-500/40 overflow-hidden transition-all hover:-translate-y-1 group"
      style={{ background: 'linear-gradient(160deg, rgba(8,145,178,0.05), var(--ax-card-bg-soft))' }}
      data-testid={`arenax-venue-card-${venue.id}`}>
      <div className="aspect-[16/9] relative overflow-hidden" style={{
        background: venue.cover_image_url
          ? `url(${venue.cover_image_url}) center/cover`
          : 'linear-gradient(135deg, #0e7490, #f59e0b)',
      }}>
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent" />
        <div className="absolute top-3 left-3 flex flex-wrap gap-1">
          {(venue.sports_supported || []).slice(0, 2).map(s => (
            <span key={s} className="text-[10px] font-bold px-2 py-1 rounded-md bg-cyan-500/20 backdrop-blur-md border border-cyan-500/30 text-cyan-200">
              {SPORT_ICONS[s] || '🏟️'} {s}
            </span>
          ))}
        </div>
      </div>
      <div className="p-4">
        <div className="text-sm font-bold text-slate-100 mb-1 group-hover:text-cyan-300 transition-colors">{venue.name}</div>
        <div className="flex items-center gap-1 text-xs text-slate-500">
          <MapPin className="h-3 w-3" />
          {venue.city || 'India'}
        </div>
      </div>
    </Link>
  );
}

// Compact live-network pulse band shown inside the hero stage card.
function HeroMiniLive() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/arenax/live-now').then(r => setData(r.data)).catch(() => {});
    const t = setInterval(() => api.get('/arenax/live-now').then(r => setData(r.data)).catch(() => {}), 30000);
    return () => clearInterval(t);
  }, []);
  const n = data?.network || {};
  return (
    <>
      <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] text-emerald-400/90 mb-4">
        <span className="relative h-2 w-2"><span className="absolute inset-0 rounded-full bg-emerald-400" /><span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping" /></span>
        LIVE · ARENAX NETWORK PULSE
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <MiniStat icon={Activity} value={n.live_now || 0} label="games live" color="#10b981" />
        <MiniStat icon={Sparkles} value={n.starting_soon || 0} label="starting <60m" color="#f59e0b" />
        <MiniStat icon={Zap} value={n.open_to_grab || 0} label="slots open" color="#06b6d4" />
        <MiniStat icon={Globe} value={(data?.cities || []).length} label="cities active" color="#ec4899" />
      </div>
      <div className="flex items-center justify-between text-[10px] text-slate-500">
        <div>
          Top venue today: <span className="text-cyan-300 font-semibold">{data?.trending?.top_venue?.venue_name || '—'}</span>
        </div>
        <Link to="/arenax/venues" className="text-amber-300 hover:text-amber-200 flex items-center gap-1">
          Find a game <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
    </>
  );
}

function MiniStat({ icon: Icon, value, label, color }) {
  return (
    <div
      className="rounded-lg p-3 border"
      style={{
        background: 'var(--ax-card-bg)',
        borderColor: 'var(--ax-border-quiet)',
      }}
    >
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="h-3 w-3" style={{ color }} />
        <span
          className="text-[9px] tracking-wider uppercase font-semibold"
          style={{ color: `hsl(var(--ax-text-muted))` }}
        >
          {label}
        </span>
      </div>
      <div className="text-xl font-black" style={{ color }}>{value}</div>
    </div>
  );
}
